import { Inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { DOCUMENT } from '@angular/common';
import {
  ComponentConfig,
  ImageTypes,
  ImgRGB,
  Languages,
  sessionStorageItems,
  PrefixLangs,
  RestrictedPages,
  RestrictionCriteriasConfig,
} from './core.models';
import { Jurisdication } from './visitor/visitor.model';
import { CategoriesPageTypeEnum } from '../game-type-games/game-type-games.models';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { UserDetails } from './user/user.model';

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  private legacyTranslations$ = new BehaviorSubject<Record<string, string>>(undefined);

  isDev = location.hostname.includes('localhost') || location.hostname.startsWith('dev-');
  isStg = location.hostname.includes('whgstage') && !location.hostname.startsWith('dev-');

  jackpotsUrl = this.isDev || this.isStg ? 'https://feeds-jackpots.s3.amazonaws.com/' : 'https://jackpots.whitehatgaming.com/';
  platformBaseUrl =
    this.isDev || this.isStg
      ? 'https://platform.whgstage.com/platform/usergateway/'
      : 'https://platform.whitehatgaming.com/platform/usergateway/';
  scontentUrl = this.isDev || this.isStg ? 'https://stage.whgstage.com/scontent' : 'https://scontent-wh.whitehatgaming.com';
  legacyUrl = this.isDev ? 'https://dev-flaxino.whgstage.com' : '';

  constructor(@Inject(DOCUMENT) private document: Document, private cookieService: CookieService, public translate: TranslateService) {}

  // TODO: rename as it not a random interval, its a random number
  randomInterval(min: number, max: number): number {
    const rand = min + Math.random() * (max + 1 - min);
    return Math.floor(rand);
  }

  filterArrayByLocation<T extends RestrictionCriteriasConfig>(element: T[], jurisdiction: Jurisdication, countryCode: string): T[] {
    return element.filter((el) => {
      return this.allowedByLocation(el, jurisdiction, countryCode);
    });
  }

  allowedByLocation<T extends RestrictionCriteriasConfig>(el: T, jurisdiction: Jurisdication, countryCode: string): boolean {
    if (!this.ifRestrictionsPresent(el)) return true;

    let showed = false;

    if (!el.excludedJurisdictions.length) {
      // * element applies for all jurisdictions
      showed = true;
    } else if (el.excludedJurisdictions.includes(jurisdiction)) {
      // * el found in excluded jurisditions array
      showed = false;
    } else {
      // * there are excluded jurisdictions for this element but the current jurisdiction is different
      showed = true;
    }

    if (showed) {
      if (!el.includedOnlyLanguages.length && !el.excludedOnlyLanguages.length) {
        // * if there are no restrictions for the element then show it everywhere
        showed = true;
      } else if (el.includedOnlyLanguages.includes(countryCode)) {
        // * show only for specific contries
        showed = true;
      } else if (el.excludedOnlyLanguages.includes(countryCode)) {
        // * hide for this countries
        showed = false;
      } else {
        // * if there are restrictions for both included and excluded but geoLocationCountryCode is not there, in this case includedOnlyLanguages takes priority
        showed = false;
      }
    }

    return showed;
  }

  /*
   * The cookie is set in legacy, the page is also refreshed therefore we can not use state
   */
  getSessionIdFromCookie() {
    return this.cookieService.get('sid');
  }

  getCurrentLangFromCookie(): string {
    return this.cookieService.get('language');
  }

  getLangPrefix(jurisdiction: string | undefined, prefix: string): string {
    return !!jurisdiction ? jurisdiction + prefix : PrefixLangs.DefaultJurisdiction + prefix;
  }

  getLangDefaultJurisdiction(jurisdiction: string | undefined): string {
    return !!jurisdiction ? jurisdiction : PrefixLangs.DefaultJurisdiction;
  }

  switchLangGetConfig(event: LangChangeEvent, jurisdiction: string | undefined, prefix: string): ComponentConfig {
    return event.translations[this.getLangDefaultJurisdiction(jurisdiction)][prefix.substring(1)];
  }

  getKeyByValue(object, value): string {
    return Object.keys(object).find((key) => object[key] === value);
  }

  updateQueryStringParameter(uri, key, value) {
    const re = new RegExp('([?&])' + key + '=.*?(&|$)', 'i');
    const separator = uri.indexOf('?') !== -1 ? '&' : '?';
    if (uri.match(re)) {
      return uri.replace(re, '$1' + key + '=' + value + '$2');
    } else {
      return uri + separator + key + '=' + value;
    }
  }

  removeHtmlTags(description: string): string {
    return description.replace(/(<([^>]+)>)/gi, '');
  }

  getImagePath(imgCode: string, type: ImageTypes = ImageTypes.Portrait) {
    return this.scontentUrl + type + imgCode + '.jpg';
  }

  getDesktopImagePath(imgCode: string): string {
    return `${this.scontentUrl}/images/gameplay/${imgCode}.jpg`;
  }

  /*
   * Because we are using Angular Elements, we can not get the current url query param by using Angular's Router,
   * Therefore using HttpParams instead
   */
  getParamValueQueryString(paramName: string) {
    const url: string = window.location.href;
    let paramValue: string;
    if (url.includes('?')) {
      /*
       *   We get the first value after '?'
       */
      const httpParams: HttpParams = new HttpParams({
        fromString: url.split('?')[1],
      });
      paramValue = httpParams.get(paramName);
    }
    return paramValue;
  }

  getUrlPath(): string {
    return window.location.pathname;
  }

  hideContentIfGameCategoryOpened(): boolean {
    return !!this.getParamValueQueryString('cat') || !!this.getParamValueQueryString('provider') || !!this.getParamValueQueryString('type');
  }

  checkCategoryOrStudioGameTypeFilter(): CategoriesPageTypeEnum {
    if (!!this.getParamValueQueryString('cat')) {
      return CategoriesPageTypeEnum.Categories;
    } else if (!!this.getParamValueQueryString('provider')) {
      return CategoriesPageTypeEnum.Provider;
    } else if (!!this.getParamValueQueryString('type')) {
      const type: string = this.getParamValueQueryString('type');
      switch (type) {
        case 'mygames':
          return CategoriesPageTypeEnum.MyGames;

        case 'recommended':
          return CategoriesPageTypeEnum.Recommended;

        case 'jackpots':
          return CategoriesPageTypeEnum.Jackpots;
      }
    }
  }

  private ifRestrictionsPresent<T extends RestrictionCriteriasConfig>(el: T): boolean {
    if (
      el.hasOwnProperty('excludedJurisdictions') &&
      el.hasOwnProperty('includedOnlyLanguages') &&
      el.hasOwnProperty('excludedOnlyLanguages')
    ) {
      return true;
    }
    return false;
  }

  setLegacyTranslations(translatedObject: Record<string, string>): void {
    this.legacyTranslations$.next(translatedObject);
  }

  getLegacyTranslation(key: string): Observable<string> {
    return this.legacyTranslations$.pipe(
      filter((x) => !!x),
      map((values) => values[key])
    );
  }

  /*
   * This method gets the average RGB color pattern,
   * This is later used to set the container img to the specified color
   * Borrowed from: http://jsfiddle.net/xLF38/818/
   */
  getAverageRGB(imgEl: HTMLImageElement): ImgRGB {
    let blockSize: number = 5, // only visit every 5 pixels
      defaultRGB: ImgRGB = { r: 0, g: 0, b: 0 }, // for non-supporting envs
      canvas: HTMLCanvasElement = document.createElement('canvas'),
      context: CanvasRenderingContext2D = canvas.getContext && canvas.getContext('2d'),
      i: number = -4,
      rgb: ImgRGB = { r: 0, g: 0, b: 0 },
      count: number = 0,
      length: number,
      data: ImageData,
      width: number,
      height: number;

    if (!context) {
      return defaultRGB;
    }

    height = canvas.height = imgEl.naturalHeight || imgEl.offsetHeight || imgEl.height;
    width = canvas.width = imgEl.naturalWidth || imgEl.offsetWidth || imgEl.width;

    /*
     * Img is then drwan to a canvas so we can call getImageData()
     */
    context.drawImage(imgEl, 0, 0);
    try {
      data = context.getImageData(0, 0, width, height);
    } catch (e) {
      return defaultRGB;
    }

    length = data.data.length;

    while ((i += blockSize * 4) < length) {
      ++count;
      rgb.r += data.data[i];
      rgb.g += data.data[i + 1];
      rgb.b += data.data[i + 2];
    }

    // ~~ used to floor values
    rgb.r = ~~(rgb.r / count);
    rgb.g = ~~(rgb.g / count);
    rgb.b = ~~(rgb.b / count);

    return rgb;
  }

  getScreenWidthFromWindow(): number {
    return window.innerWidth;
  }

  removeFirstElementByTagName(tagName: string) {
    const element = this.document.getElementsByTagName(tagName)[0];
    if (element) {
      element.remove();
    }
  }

  isModalOpenedByAriaLabel(label: string): boolean {
    return !!this.document.querySelector("[aria-labelledby='" + label + "']");
  }

  isPageFromRestrictedList(): boolean {
    return Object.values(RestrictedPages).some((path) => window.location.href.includes(path));
  }

  getCurrentLang(): string {
    return Languages[this.translate.currentLang.toUpperCase()];
  }

  switchLanguage(lang: string): void {
    // TODO-A If testing on localhost then uncomment the code below and comment the location.replace one
    // this.translate.use(this.coreService.getKeyByValue(Languages, lang).toLowerCase());
    location.replace(this.updateQueryStringParameter(window.location.href, 'language', this.getKeyByValue(Languages, lang).toLowerCase()));
  }

  isCurrentPathContains(urlPage: string): boolean {
    return window.location.href.indexOf(urlPage) > -1;
  }

  currencySymbolReplaceInText(content: any, symbol: string): string {
    return content.includes('{{ symbol }}') ? content.replaceAll('{{ symbol }}', symbol) : content;
  }

  /*
   * Since we are getting currency symbol from legacy User::getCountry() and hardcoding monetary values,
   * We need a method to actually convert these values in text, this is done by finding the amount in
   * {{ convertCurrency(amount) }} from within our tranlsation module
   */
  getCurrencyAmountFromText(content: any): string {
    let i = content.indexOf('convertCurrency(');
    if (i >= 0) {
      content = content.substring(i + 'convertCurrency('.length);
    } else {
      return '';
    }
    i = content.indexOf(') }}');
    if (i >= 0) {
      content = content.substring(0, i);
    } else {
      return '';
    }
    return content;
  }

  currencyAmountReplaceInText(content: any, symbol: string, newAmount: string): string {
    const currentAmount = this.getCurrencyAmountFromText(content);
    return content.includes(`{{ convertCurrency(${currentAmount}) }}`)
      ? content.replaceAll(`{{ convertCurrency(${currentAmount}) }}`, `${symbol}${newAmount}`)
      : content;
  }

  sanitizeCanadianText(content: any, countryCode: string): string {
    if (countryCode === 'CA') {
      /*
       * These games are no longer available to canadian players
       */
      let contentSanitized = content.includes('Starburst') ? content.replaceAll('Starburst', 'Immortal Romance') : content;
      contentSanitized = contentSanitized.includes('Gonzo’s Quest')
        ? contentSanitized.replaceAll('Gonzo’s Quest', 'Wild Scarabs')
        : contentSanitized;
      contentSanitized = contentSanitized.includes('Gonzo&rsquo;s Quest')
        ? contentSanitized.replaceAll('Gonzo&rsquo;s Quest', 'Wild Scarabs')
        : contentSanitized;
      contentSanitized = contentSanitized.includes('Twin Spin')
        ? contentSanitized.replaceAll('Twin Spin', 'Book of Dead')
        : contentSanitized;
      contentSanitized = contentSanitized.includes('Aloha! Cluster Pays')
        ? contentSanitized.replaceAll('Aloha! Cluster Pays', 'Fortunium')
        : contentSanitized;
      contentSanitized = contentSanitized.includes('4. This offer is not available to customers residing in the United Kingdom and Canada.')
        ? contentSanitized.replaceAll(
            '4. This offer is not available to customers residing in the United Kingdom and Canada.',
            '4. This offer is only available to customers residing in Canada.'
          )
        : contentSanitized;
      return contentSanitized;
    }
    return content;
  }

  getUserDetails(): UserDetails {
    return JSON.parse(sessionStorage.getItem(sessionStorageItems.UserDetails));
  }

  hasCachedUserDetails(): Boolean {
    return sessionStorage.hasOwnProperty(sessionStorageItems.UserDetails);
  }

  clearUserDetails(): void {
    sessionStorage.removeItem(sessionStorageItems.UserDetails);
  }

  /*
   * @TODO-J Getting rates from cms, why are these not on platform ? are we not using the same exchange rates across all brands ?
   * functionality here is equivelent to ($amount / $this->exchange_rates[$from]) * $this->exchange_rates[$to];
   * coming from legacy currency.php
   */
  convertCurrencyAmount(amount: number, currencyCode: string, exchangeRates?: any): number {
    const ratesFromCms = JSON.parse(exchangeRates);
    const rate: number = (amount / +ratesFromCms['EUR']) * +ratesFromCms[currencyCode];
    return rate;
  }

  addModalStateHistory(): void {
    if (!this.isModalOpenedByAriaLabel('search-modal-title')) {
      const modalState = {
        modal: true,
        desc: 'modal opened',
      };
      history.pushState(modalState, null);
    }
  }
}
